import {
  DateString,
  fullDayMonthDateString,
  timeStringFromDate,
  toDateString,
} from '@nextforce/api'
import { makeStyles, Text, useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { GoldStarLogs, GoldStarUserLogs } from 'gold_stars/queries/types'
import { isEmpty } from 'lodash'
import { useCallback, useMemo } from 'react'
import { SectionList, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { PrevNext } from 'shared/components/non_availability/header_prev_next'
import { UserAvatar } from 'shared/components/user/user_avatar'

interface GoldStarChangesViewProps {
  goldStarChanges?: GoldStarUserLogs
  currentDate: moment.Moment
  onPrevMonth: () => void
  onNextMonth: () => void
  canGoNext: boolean
  canGoPrev: boolean
}

interface Section {
  date: DateString
  data: GoldStarLogs[]
  index: number
}

export function GoldStarChangesView({
  goldStarChanges,
  currentDate,
  onPrevMonth,
  onNextMonth,
  canGoNext,
  canGoPrev,
}: GoldStarChangesViewProps) {
  const { theme } = useTheme()
  const styles = useStyles(theme)

  const changes = useMemo(
    () => goldStarChanges?.goldStarUserLogs ?? [],
    [goldStarChanges]
  )

  const sections: Section[] = useMemo(
    () =>
      Object.entries(
        changes.reduce((acc, change) => {
          const date = toDateString(change.changedAt)
          return {
            ...acc,
            [date]: [...(acc[date] || []), change],
          }
        }, {} as { [key: string]: GoldStarLogs[] })
      ).map(([date, data], index) => ({
        date,
        data,
        index,
      })),
    [changes]
  )

  const renderItem = useCallback(
    ({ item }: { item: GoldStarLogs }) => {
      if (!item) {
        return null
      }
      return (
        <View style={styles.userChangeItem}>
          <UserAvatar
            user={item?.fromUser}
            size={'small'}
            showAvatarIfNoPhone
          />
          <View style={styles.changeDescriptionContainer}>
            <Text>{`${item.fromUser?.firstName} ${item.fromUser?.lastName} ${
              item.type
            } ${item?.starCount ?? 0} ${t('star', {
              count: item?.starCount ?? 0,
            })} ${t('to')} ${item.user?.firstName} ${
              item.user?.lastName
            }`}</Text>
          </View>
          <Text style={styles.time}>{timeStringFromDate(item.changedAt)}</Text>
        </View>
      )
    },
    [styles.userChangeItem, styles.changeDescriptionContainer, styles.time]
  )

  return (
    <SafeAreaView edges={['left', 'right', 'bottom']} style={styles.root}>
      <View style={styles.container}>
        <View style={{ alignItems: 'center' }}>
          <PrevNext
            onPressNext={onNextMonth}
            onPressPrev={onPrevMonth}
            disabledNext={!canGoNext}
            disabledPrev={!canGoPrev}
          >
            <Text>{currentDate.format('MMMM YYYY')}</Text>
          </PrevNext>
        </View>
        <EmptyGoldStarChanges goldStarChanges={goldStarChanges} />
        <SectionList
          sections={sections}
          ListHeaderComponent={
            <View style={styles.headerContainer}>
              {goldStarChanges?.allocatedStarCount ? (
                <Text>
                  {t('currentMonthStarAllocation', {
                    starCount: goldStarChanges?.allocatedStarCount,
                  })}
                </Text>
              ) : null}
              {goldStarChanges?.previousMonthStarBalance ? (
                <Text>
                  {t('previousMonthStarBalance', {
                    starCount: goldStarChanges?.previousMonthStarBalance,
                  })}
                </Text>
              ) : null}
            </View>
          }
          renderSectionHeader={({ section: { date } }) => (
            <View style={styles.dateHeaderContainer}>
              <Text style={styles.dateHeader}>
                {fullDayMonthDateString(date)}
              </Text>
            </View>
          )}
          renderItem={renderItem}
        />
      </View>
    </SafeAreaView>
  )
}

function EmptyGoldStarChanges({
  goldStarChanges,
}: {
  goldStarChanges?: GoldStarUserLogs
}) {
  const styles = useStyles()
  return isEmpty(goldStarChanges) ? (
    <View style={styles.emptyContainer}>
      <Text>{t('noChangesThisMonth')}</Text>
    </View>
  ) : null
}

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
  },
  container: {
    flex: 1,
    padding: 5,
    margin: 10,
  },
  dateHeaderContainer: {
    alignItems: 'center',
    marginVertical: 10,
  },
  headerContainer: {
    alignItems: 'center',
    padding: 10,
    margin: 10,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  dateHeader: {
    fontSize: 14,
    backgroundColor: theme.colors.grey7,
    paddingVertical: 5,
    paddingHorizontal: 10,
    borderRadius: 5,
  },
  userChangeItem: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 10,
  },
  time: {
    alignSelf: 'flex-start',
    fontSize: 12,
    backgroundColor: theme.colors.grey7,
    borderRadius: 15,
    paddingVertical: 2,
    width: 70,
    textAlign: 'center',
    marginLeft: 5,
  },
  changeDescriptionContainer: {
    flex: 1,
    paddingLeft: 10,
  },
}))
